<template>
  <section class="template-page">
    <header class="page-head">
      <div>
        <h1>喂食计划模板</h1>
        <p>统一配置新建宠物时可选的喂食周期，小程序会按分类和品种自动匹配。</p>
      </div>
      <button type="button" class="primary-button" @click="openCreate">+  新建模板</button>
    </header>

    <div class="summary-strip">
      <div class="summary-item">
        <strong>{{ templates.length }}</strong>
        <span>全部模板</span>
      </div>
      <div class="summary-item">
        <strong>{{ enabledCount }}</strong>
        <span>启用中</span>
      </div>
      <div class="summary-item">
        <strong>{{ speciesTemplateCount }}</strong>
        <span>品种专用</span>
      </div>
      <div class="summary-note">匹配优先级：品种专用 &gt; 分类通用 &gt; 全局通用</div>
    </div>

    <div class="toolbar">
      <label class="search-field">
        <span class="search-icon">⌕</span>
        <input v-model.trim="filters.keyword" placeholder="搜索模板、食物或适用范围" />
      </label>
      <select v-model="filters.groupCode">
        <option value="">全部分类</option>
        <option v-for="item in categoryTree" :key="item.typeCode" :value="item.typeCode">{{ item.typeName }}</option>
      </select>
      <select v-model="filters.status">
        <option value="">全部状态</option>
        <option value="1">启用</option>
        <option value="0">停用</option>
      </select>
      <button type="button" class="ghost-button" @click="resetFilters">重置</button>
    </div>

    <div class="table-shell">
      <div v-if="loading" class="empty-state">正在加载模板...</div>
      <div v-else-if="filteredTemplates.length === 0" class="empty-state">
        <strong>还没有匹配的模板</strong>
        <span>调整筛选条件，或新建第一个喂食计划模板。</span>
      </div>
      <table v-else>
        <thead>
          <tr>
            <th>模板</th>
            <th>适用范围</th>
            <th>投喂规则</th>
            <th>生成周期</th>
            <th>状态</th>
            <th class="actions-column">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in filteredTemplates" :key="item.id">
            <td>
              <strong class="template-name">{{ item.templateName }}</strong>
              <span class="muted-line">{{ item.description || "暂无说明" }}</span>
            </td>
            <td>
              <span class="scope-chip">{{ item.scopeText }}</span>
            </td>
            <td>
              <strong>{{ item.foodName }} {{ formatCount(item.feedingCount) }}{{ item.feedingUnit }}</strong>
              <span class="muted-line">每 {{ item.intervalDays }} 天 · {{ item.slotText }}</span>
            </td>
            <td>{{ item.durationDays }} 天</td>
            <td>
              <span class="status-dot" :class="item.status === 1 ? 'status-dot--active' : ''"></span>
              {{ item.status === 1 ? "启用" : "停用" }}
            </td>
            <td class="actions-column">
              <button type="button" class="text-button" @click="openEdit(item)">编辑</button>
              <button type="button" class="text-button text-button--danger" @click="removeTemplate(item)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="editorVisible" class="drawer-mask" @click.self="closeEditor">
      <aside class="editor-drawer">
        <header class="editor-head">
          <div>
            <span class="editor-eyebrow">{{ editingId ? "编辑模板" : "新建模板" }}</span>
            <h2>{{ draft.templateName || "未命名喂食计划" }}</h2>
          </div>
          <button type="button" class="close-button" aria-label="关闭" @click="closeEditor">×</button>
        </header>

        <form class="editor-form" @submit.prevent="saveTemplate">
          <section class="form-section">
            <h3>基本信息</h3>
            <label class="field field--full">
              <span>模板名称 *</span>
              <input v-model.trim="draft.templateName" maxlength="120" placeholder="例如：豹纹守宫入门计划" />
            </label>
            <div class="field-grid">
              <label class="field">
                <span>适用分类</span>
                <select v-model="draft.speciesGroupCode" @change="onDraftGroupChange">
                  <option value="">全部宠物</option>
                  <option v-for="item in categoryTree" :key="item.typeCode" :value="item.typeCode">{{ item.typeName }}</option>
                </select>
              </label>
              <label class="field">
                <span>适用品种</span>
                <select v-model="draft.speciesCode" :disabled="!draft.speciesGroupCode" @change="onDraftScopeChange">
                  <option value="">{{ draft.speciesGroupCode ? "分类下全部品种" : "请先选分类" }}</option>
                  <option v-for="item in draftSpeciesOptions" :key="item.typeCode" :value="item.typeCode">{{ item.typeName }}</option>
                </select>
              </label>
              <label class="field">
                <span>适用阶段</span>
                <select v-model="draft.stage" @change="onDraftScopeChange">
                  <option value="">全部阶段</option>
                  <option value="幼体">幼体</option>
                  <option value="亚成体">亚成体</option>
                  <option value="成体">成体</option>
                </select>
              </label>
              <label class="field">
                <span>状态</span>
                <select v-model.number="draft.status">
                  <option :value="1">启用</option>
                  <option :value="0">停用</option>
                </select>
              </label>
            </div>
          </section>

          <section class="ai-assistant-panel" :class="aiNotice ? 'ai-assistant-panel--ready' : ''">
            <div class="ai-assistant-head">
              <div class="ai-assistant-title">
                <span class="ai-badge">AI</span>
                <div>
                  <strong>生成模板草稿</strong>
                  <span>{{ draftScopeText }}</span>
                </div>
              </div>
              <div class="ai-assistant-actions">
                <span>只填草稿</span>
                <button
                  type="button"
                  class="ai-generate-button"
                  :disabled="aiGenerating || !draft.speciesGroupCode"
                  @click="generateAiDraft"
                >{{ aiGenerating ? "生成中..." : "AI 生成草稿" }}</button>
              </div>
            </div>
            <textarea
              v-model.trim="aiRequirements"
              maxlength="500"
              rows="2"
              placeholder="补充要求（可选），例如：适合成体、夜间投喂、计划覆盖 60 天"
            ></textarea>
            <p v-if="!draft.speciesGroupCode" class="ai-assistant-message">请先选择适用分类</p>
            <p v-else-if="aiNotice" class="ai-assistant-message ai-assistant-message--ready">{{ aiNotice }}</p>
          </section>

          <section class="form-section">
            <h3>投喂规则</h3>
            <div class="field-grid field-grid--three">
              <label class="field field--wide">
                <span>默认食物 *</span>
                <input v-model.trim="draft.foodName" maxlength="120" placeholder="例如：蟋蟀" />
              </label>
              <label class="field">
                <span>单次数量 *</span>
                <input v-model.number="draft.feedingCount" type="number" min="0.01" step="0.01" />
              </label>
              <label class="field">
                <span>单位 *</span>
                <input v-model.trim="draft.feedingUnit" maxlength="32" placeholder="只 / g / 份" />
              </label>
              <label class="field">
                <span>间隔天数 *</span>
                <input v-model.number="draft.intervalDays" type="number" min="1" max="90" />
              </label>
              <label class="field">
                <span>投喂时段 *</span>
                <select v-model="draft.slot">
                  <option value="AM">早上</option>
                  <option value="PM">下午</option>
                  <option value="NIGHT">晚上</option>
                </select>
              </label>
              <label class="field">
                <span>生成计划天数 *</span>
                <input v-model.number="draft.durationDays" type="number" min="1" max="365" />
              </label>
              <label class="field">
                <span>排序</span>
                <input v-model.number="draft.sortNo" type="number" min="0" />
              </label>
            </div>
            <div class="rule-preview">
              <span>计划预览</span>
              <strong>{{ draftSummary }}</strong>
            </div>
          </section>

          <section class="form-section">
            <h3>照护说明</h3>
            <label class="field field--full">
              <span>份量补充</span>
              <input v-model.trim="draft.portionText" maxlength="255" placeholder="例如：食物尺寸不超过头部宽度" />
            </label>
            <label class="field field--full">
              <span>补剂安排</span>
              <input v-model.trim="draft.supplementText" maxlength="255" placeholder="例如：每周一次钙粉" />
            </label>
            <label class="field field--full">
              <span>观察重点</span>
              <input v-model.trim="draft.observationFocus" maxlength="255" placeholder="例如：观察食欲和排便" />
            </label>
            <label class="field field--full">
              <span>模板说明</span>
              <textarea v-model.trim="draft.description" maxlength="500" rows="3" placeholder="后台备注和小程序展示说明"></textarea>
            </label>
          </section>

          <p v-if="errorMessage" class="form-message form-message--error">{{ errorMessage }}</p>
          <footer class="editor-actions">
            <button type="button" class="ghost-button" @click="closeEditor">取消</button>
            <button type="submit" class="primary-button" :disabled="saving || aiGenerating">{{ saving ? "保存中..." : "保存模板" }}</button>
          </footer>
        </form>
      </aside>
    </div>
  </section>
</template>

<script>
import {
  createFeedingPlanTemplate,
  deleteFeedingPlanTemplate,
  fetchFeedingPlanTemplateList,
  generateFeedingPlanTemplateDraft,
  updateFeedingPlanTemplate
} from "../api/feedingPlanTemplate"
import { fetchPetTypeTree } from "../api/petType"

function createEmptyDraft() {
  return {
    templateName: "",
    speciesGroupCode: "",
    speciesCode: "",
    stage: "",
    foodName: "",
    feedingCount: 1,
    feedingUnit: "只",
    intervalDays: 3,
    slot: "NIGHT",
    durationDays: 30,
    portionText: "",
    supplementText: "",
    observationFocus: "",
    description: "",
    status: 1,
    sortNo: 100
  }
}

export default {
  name: "FeedingPlanView",
  data() {
    return {
      loading: false,
      saving: false,
      aiGenerating: false,
      templates: [],
      categoryTree: [],
      filters: { keyword: "", groupCode: "", status: "" },
      editorVisible: false,
      editingId: 0,
      draft: createEmptyDraft(),
      aiRequirements: "",
      aiNotice: "",
      errorMessage: ""
    }
  },
  computed: {
    enabledCount() {
      return this.templates.filter(item => item.status === 1).length
    },
    speciesTemplateCount() {
      return this.templates.filter(item => item.speciesCode).length
    },
    filteredTemplates() {
      const keyword = this.filters.keyword.toLowerCase()
      return this.templates.filter(item => {
        const haystack = [item.templateName, item.foodName, item.scopeText, item.description].join(" ").toLowerCase()
        const matchesKeyword = !keyword || haystack.includes(keyword)
        const matchesGroup = !this.filters.groupCode || item.speciesGroupCode === this.filters.groupCode
        const matchesStatus = this.filters.status === "" || String(item.status) === this.filters.status
        return matchesKeyword && matchesGroup && matchesStatus
      })
    },
    draftSpeciesOptions() {
      const group = this.categoryTree.find(item => item.typeCode === this.draft.speciesGroupCode)
      return group && group.children ? group.children : []
    },
    draftScopeText() {
      const group = this.categoryTree.find(item => item.typeCode === this.draft.speciesGroupCode)
      if (!group) return "选择适用范围后生成"
      const species = (group.children || []).find(item => item.typeCode === this.draft.speciesCode)
      const scope = species ? species.typeName : `${group.typeName} · 全部品种`
      return this.draft.stage ? `${scope} · ${this.draft.stage}` : scope
    },
    draftSummary() {
      const slotText = { AM: "早上", PM: "下午", NIGHT: "晚上" }[this.draft.slot] || "晚上"
      const food = this.draft.foodName || "指定食物"
      return `每 ${this.draft.intervalDays || 0} 天投喂 ${food} ${this.draft.feedingCount || 0}${this.draft.feedingUnit || ""} · ${slotText} · ${this.draft.durationDays || 0} 天`
    }
  },
  created() {
    this.loadPage()
  },
  methods: {
    async loadPage() {
      this.loading = true
      try {
        const [templateRes, typeRes] = await Promise.all([
          fetchFeedingPlanTemplateList(),
          fetchPetTypeTree()
        ])
        this.templates = templateRes.data || []
        this.categoryTree = (typeRes.data && typeRes.data.items) || []
      } catch (error) {
        window.alert(error.message || "页面数据加载失败")
      } finally {
        this.loading = false
      }
    },
    resetFilters() {
      this.filters = { keyword: "", groupCode: "", status: "" }
    },
    openCreate() {
      this.editingId = 0
      this.draft = createEmptyDraft()
      this.aiRequirements = ""
      this.aiNotice = ""
      this.errorMessage = ""
      this.editorVisible = true
    },
    openEdit(item) {
      this.editingId = item.id
      this.draft = {
        templateName: item.templateName || "",
        speciesGroupCode: item.speciesGroupCode || "",
        speciesCode: item.speciesCode || "",
        stage: item.stage || "",
        foodName: item.foodName || "",
        feedingCount: Number(item.feedingCount || 1),
        feedingUnit: item.feedingUnit || "只",
        intervalDays: item.intervalDays || 3,
        slot: item.slot || "NIGHT",
        durationDays: item.durationDays || 30,
        portionText: item.portionText || "",
        supplementText: item.supplementText || "",
        observationFocus: item.observationFocus || "",
        description: item.description || "",
        status: item.status === 0 ? 0 : 1,
        sortNo: item.sortNo == null ? 100 : item.sortNo
      }
      this.aiRequirements = ""
      this.aiNotice = ""
      this.errorMessage = ""
      this.editorVisible = true
    },
    closeEditor() {
      if (this.saving || this.aiGenerating) return
      this.editorVisible = false
    },
    onDraftGroupChange() {
      this.draft.speciesCode = ""
      this.aiNotice = ""
    },
    onDraftScopeChange() {
      this.aiNotice = ""
    },
    async generateAiDraft() {
      if (!this.draft.speciesGroupCode || this.aiGenerating) return
      this.aiGenerating = true
      this.errorMessage = ""
      this.aiNotice = ""
      try {
        const response = await generateFeedingPlanTemplateDraft({
          speciesGroupCode: this.draft.speciesGroupCode,
          speciesCode: this.draft.speciesCode || null,
          stage: this.draft.stage || null,
          requirements: this.aiRequirements || null
        })
        const result = response.data || {}
        this.draft = {
          ...this.draft,
          templateName: result.templateName || this.draft.templateName,
          foodName: result.foodName || this.draft.foodName,
          feedingCount: Number(result.feedingCount || this.draft.feedingCount),
          feedingUnit: result.feedingUnit || this.draft.feedingUnit,
          intervalDays: Number(result.intervalDays || this.draft.intervalDays),
          slot: result.slot || this.draft.slot,
          durationDays: Number(result.durationDays || this.draft.durationDays),
          portionText: result.portionText || "",
          supplementText: result.supplementText || "",
          observationFocus: result.observationFocus || "",
          description: result.description || ""
        }
        this.aiNotice = result.generationNotice || "AI 草稿已回填，请审核后保存。"
      } catch (error) {
        this.errorMessage = error.message || "AI 草稿生成失败"
      } finally {
        this.aiGenerating = false
      }
    },
    validateDraft() {
      if (!this.draft.templateName) return "请填写模板名称"
      if (!this.draft.foodName) return "请填写默认食物"
      if (!(Number(this.draft.feedingCount) > 0)) return "投喂数量必须大于 0"
      if (!this.draft.feedingUnit) return "请填写投喂单位"
      if (!(Number(this.draft.intervalDays) >= 1)) return "间隔天数不能小于 1"
      if (!(Number(this.draft.durationDays) >= 1)) return "生成计划天数不能小于 1"
      return ""
    },
    async saveTemplate() {
      this.errorMessage = this.validateDraft()
      if (this.errorMessage) return
      this.saving = true
      try {
        if (this.editingId) {
          await updateFeedingPlanTemplate(this.editingId, this.draft)
        } else {
          await createFeedingPlanTemplate(this.draft)
        }
        this.editorVisible = false
        await this.loadPage()
      } catch (error) {
        this.errorMessage = error.message || "模板保存失败"
      } finally {
        this.saving = false
      }
    },
    async removeTemplate(item) {
      if (!window.confirm(`确认删除「${item.templateName}」？已生成的用户计划不受影响。`)) return
      try {
        await deleteFeedingPlanTemplate(item.id)
        await this.loadPage()
      } catch (error) {
        window.alert(error.message || "模板删除失败")
      }
    },
    formatCount(value) {
      const number = Number(value || 0)
      return Number.isInteger(number) ? String(number) : String(number).replace(/0+$/, "").replace(/\.$/, "")
    }
  }
}
</script>

<style scoped>
.template-page { color: #18201b; }
.page-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 18px; }
.page-head h1 { margin: 0; font-size: 24px; line-height: 32px; letter-spacing: 0; }
.page-head p { margin: 6px 0 0; color: #758078; font-size: 13px; }
.primary-button, .ghost-button, .text-button, .close-button { border: 0; font: inherit; cursor: pointer; }
.primary-button { min-height: 38px; padding: 0 16px; border-radius: 6px; background: #17241b; color: #fff; font-size: 13px; font-weight: 600; }
.primary-button:disabled { cursor: wait; opacity: .55; }
.ghost-button { min-height: 36px; padding: 0 14px; border: 1px solid #dfe5e0; border-radius: 6px; background: #fff; color: #39443c; }
.summary-strip { display: grid; grid-template-columns: 140px 140px 140px minmax(260px, 1fr); min-height: 86px; margin-bottom: 14px; border: 1px solid #e5eae6; border-radius: 8px; background: #fff; }
.summary-item { display: flex; flex-direction: column; justify-content: center; padding: 0 22px; border-right: 1px solid #edf0ee; }
.summary-item strong { font-size: 24px; line-height: 28px; }
.summary-item span { margin-top: 3px; color: #849087; font-size: 12px; }
.summary-note { display: flex; align-items: center; justify-content: flex-end; padding: 0 22px; color: #6f7a72; font-size: 12px; }
.toolbar { display: flex; align-items: center; gap: 10px; margin-bottom: 14px; }
.toolbar select, .toolbar input, .field input, .field select, .field textarea { border: 1px solid #dfe5e0; border-radius: 6px; background: #fff; color: #202a23; outline: none; box-sizing: border-box; }
.toolbar select { width: 150px; height: 38px; padding: 0 10px; }
.search-field { position: relative; flex: 1; max-width: 420px; }
.search-field input { width: 100%; height: 38px; padding: 0 12px 0 34px; }
.search-icon { position: absolute; left: 12px; top: 9px; color: #879188; }
.table-shell { min-height: 360px; overflow: hidden; border: 1px solid #e4e9e5; border-radius: 8px; background: #fff; }
table { width: 100%; border-collapse: collapse; table-layout: fixed; }
th { padding: 12px 16px; background: #f7f9f7; color: #758078; font-size: 12px; font-weight: 600; text-align: left; }
td { padding: 15px 16px; border-top: 1px solid #edf0ee; color: #344039; font-size: 13px; vertical-align: middle; }
th:nth-child(1) { width: 22%; } th:nth-child(2) { width: 16%; } th:nth-child(3) { width: 23%; } th:nth-child(4) { width: 11%; } th:nth-child(5) { width: 10%; }
.template-name, td strong { display: block; color: #1d2821; font-size: 13px; }
.muted-line { display: block; margin-top: 4px; overflow: hidden; color: #8a948c; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.scope-chip { display: inline-flex; padding: 4px 8px; border-radius: 4px; background: #eef4ee; color: #426048; font-size: 11px; }
.status-dot { display: inline-block; width: 7px; height: 7px; margin-right: 6px; border-radius: 50%; background: #b9c0bb; }
.status-dot--active { background: #4d8a5b; }
.actions-column { text-align: right; }
.text-button { padding: 4px 5px; background: transparent; color: #315d3b; font-size: 12px; }
.text-button--danger { color: #b64d4d; }
.empty-state { display: flex; min-height: 360px; align-items: center; justify-content: center; flex-direction: column; color: #8a948c; font-size: 13px; }
.empty-state strong { margin-bottom: 6px; color: #354139; font-size: 15px; }
.drawer-mask { position: fixed; z-index: 100; inset: 0; display: flex; justify-content: flex-end; background: rgba(20, 28, 22, .28); }
.editor-drawer { width: min(720px, 92vw); height: 100vh; overflow-y: auto; background: #f7f9f7; box-shadow: -16px 0 40px rgba(25, 33, 27, .14); }
.editor-head { position: sticky; z-index: 2; top: 0; display: flex; min-height: 78px; align-items: center; justify-content: space-between; padding: 0 24px; border-bottom: 1px solid #e3e8e4; background: #fff; }
.editor-eyebrow { color: #78827b; font-size: 11px; }
.editor-head h2 { margin: 4px 0 0; font-size: 18px; letter-spacing: 0; }
.close-button { width: 34px; height: 34px; border-radius: 50%; background: #f0f3f0; color: #4c574f; font-size: 22px; }
.editor-form { padding: 18px 24px 28px; }
.ai-assistant-panel { margin-bottom: 14px; padding: 16px 18px; border: 1px solid #dce4ee; border-radius: 8px; background: #f7f9fc; }
.ai-assistant-panel--ready { border-color: #cbdcca; background: #f5f9f5; }
.ai-assistant-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.ai-assistant-title { display: flex; min-width: 0; align-items: center; gap: 10px; }
.ai-assistant-title div { display: flex; min-width: 0; flex-direction: column; gap: 3px; }
.ai-assistant-title strong { font-size: 14px; }
.ai-assistant-title span { overflow: hidden; color: #7d878f; font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.ai-badge { display: inline-flex; width: 34px; height: 34px; flex: 0 0 34px; align-items: center; justify-content: center; border-radius: 6px; background: #25354a; color: #fff; font-size: 11px; font-weight: 700; }
.ai-assistant-actions { display: flex; flex: 0 0 auto; align-items: center; gap: 10px; }
.ai-assistant-actions > span { color: #8a939a; font-size: 10px; }
.ai-generate-button { min-height: 36px; padding: 0 14px; border: 0; border-radius: 6px; background: #25354a; color: #fff; font: inherit; font-size: 12px; font-weight: 600; cursor: pointer; }
.ai-generate-button:disabled { cursor: not-allowed; opacity: .45; }
.ai-assistant-panel textarea { width: 100%; min-height: 58px; margin-top: 13px; padding: 9px 10px; resize: vertical; border: 1px solid #dce3ea; border-radius: 6px; background: #fff; box-sizing: border-box; color: #283039; font: inherit; font-size: 12px; outline: none; }
.ai-assistant-message { margin: 8px 0 0; color: #8a9298; font-size: 11px; }
.ai-assistant-message--ready { color: #46704c; }
.form-section { margin-bottom: 14px; padding: 18px; border: 1px solid #e3e8e4; border-radius: 8px; background: #fff; }
.form-section h3 { margin: 0 0 15px; font-size: 14px; }
.field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.field-grid--three { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.field-grid--three .field--wide { grid-column: 1 / -1; }
.field { display: flex; min-width: 0; flex-direction: column; gap: 6px; }
.field--full { margin-bottom: 14px; }
.field--wide { grid-column: span 1; }
.field span { color: #68736b; font-size: 11px; }
.field input, .field select { width: 100%; height: 38px; padding: 0 10px; }
.field textarea { width: 100%; padding: 10px; resize: vertical; font: inherit; }
.field select:disabled { background: #f3f5f3; color: #a4aba6; }
.rule-preview { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-top: 16px; padding: 11px 13px; border-left: 3px solid #6c8d70; background: #f3f7f3; font-size: 12px; }
.rule-preview span { color: #78827b; }
.rule-preview strong { text-align: right; }
.form-message { margin: 0 0 12px; padding: 10px 12px; border-radius: 6px; font-size: 12px; }
.form-message--error { background: #fff0f0; color: #a33f3f; }
.editor-actions { display: flex; justify-content: flex-end; gap: 10px; }
@media (max-width: 980px) {
  .summary-strip { grid-template-columns: repeat(3, 1fr); }
  .summary-note { display: none; }
  .toolbar { flex-wrap: wrap; }
  .search-field { min-width: 100%; max-width: none; }
}
@media (max-width: 640px) {
  .page-head { flex-direction: column; }
  .summary-strip { grid-template-columns: repeat(3, 1fr); }
  .summary-item { padding: 0 12px; }
  .field-grid, .field-grid--three { grid-template-columns: 1fr; }
  .ai-assistant-head { align-items: flex-start; flex-direction: column; }
  .ai-assistant-actions { width: 100%; }
  .ai-generate-button { width: 100%; }
  .table-shell { overflow-x: auto; }
  table { min-width: 820px; }
}
</style>
